// ============================================================
// PREPARATION ANALYTICS — Restaurant prep time performance
// ============================================================

import React, { useState, useEffect } from 'react';
import { Timer, TrendingUp, Award, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine, Legend,
} from 'recharts';
import { Button, StatCard, Table, Badge } from '../components/ui';
import { getPreparationAnalytics, getPreparationTimeData } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatNumber } from '../utils/helpers';

const TARGET_PREP_MINUTES = 20;

const EffBadge = ({ pct }) => {
  const color = pct >= 90 ? '#10B981' : pct >= 70 ? '#F59E0B' : '#EF4444';
  return (
    <span style={{ fontWeight: 700, color }}>{pct.toFixed(0)}%</span>
  );
};

const CustomBar = (props) => {
  const { x, y, width, height, value } = props;
  const fill = value <= TARGET_PREP_MINUTES ? '#10B981' : value <= 30 ? '#F59E0B' : '#EF4444';
  return <rect x={x} y={y} width={width} height={height} fill={fill} rx={3} />;
};

const PreparationAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [trendData, setTrend]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('overview');

  const load = async () => {
    setLoading(true);
    const [data, trend] = await Promise.all([
      adminApi.getPreparationAnalytics(getPreparationAnalytics),
      adminApi.getRevenueAnalytics('weekly', getPreparationTimeData),
    ]);
    setAnalytics(data || getPreparationAnalytics());
    setTrend(Array.isArray(trend) ? trend : getPreparationTimeData());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading || !analytics) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#64748B' }}>Loading analytics…</div>;
  }

  const { restaurantBreakdown = [], platformAvg, onTargetRate, mostEfficientRestaurant, totalOrders } = analytics;

  const barData = restaurantBreakdown.map(r => ({
    name: r.name.split(' ').slice(-1)[0], // short name
    fullName: r.name,
    avgPrepTime: r.avgPrepTime,
  }));

  const columns = [
    { key: 'name',         label: 'Restaurant', render: v => <b>{v}</b> },
    { key: 'avgPrepTime',  label: 'Avg Prep Time', render: v => (
      <span style={{ fontWeight: 700, color: v <= TARGET_PREP_MINUTES ? '#10B981' : v <= 30 ? '#F59E0B' : '#EF4444' }}>
        {v.toFixed(1)} min
      </span>
    )},
    { key: 'onTimeRate',   label: 'On-Time %',   render: v => <EffBadge pct={v} /> },
    { key: 'efficiency',   label: 'Efficiency',  render: (_, row) => (
      <div style={{ width: 100, background: 'var(--admin-border)', borderRadius: 4, height: 6 }}>
        <div style={{ width: `${Math.min(row.onTimeRate, 100)}%`, height: '100%', borderRadius: 4,
          background: row.onTimeRate >= 90 ? '#10B981' : row.onTimeRate >= 70 ? '#F59E0B' : '#EF4444' }} />
      </div>
    )},
    { key: 'peakHour',     label: 'Peak Hour',   render: v => <code>{v}</code> },
    { key: 'ordersCount',  label: 'Orders',      render: v => formatNumber(v) },
    { key: 'onTimeRate',   label: 'Status',      render: v => (
      <Badge status={v >= 85 ? 'active' : v >= 60 ? 'pending' : 'danger'}
        text={v >= 85 ? 'On Target' : v >= 60 ? 'Moderate' : 'Needs Help'} />
    )},
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Preparation Analytics</h1>
          <p className="admin-page-subtitle">Restaurant preparation time performance and efficiency</p>
        </div>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
      </div>

      {/* Summary */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Platform Avg Prep" value={`${platformAvg?.toFixed(1) || '—'} min`}
          icon={<Timer size={18} />} color="#8B5CF6" />
        <StatCard label="On Target (<20 min)" value={`${onTargetRate?.toFixed(0) || '—'}%`}
          icon={<TrendingUp size={18} />} color="#10B981" />
        <StatCard label="Most Efficient"     value={mostEfficientRestaurant || '—'}
          icon={<Award size={18} />} color="#F59E0B" />
        <StatCard label="Total Orders" value={formatNumber(totalOrders || 0)}
          icon={<Timer size={18} />} color="#3B82F6" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--admin-border)' }}>
        {[['overview', 'By Restaurant'], ['trend', 'Time Trend']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === key ? 700 : 400,
            color: tab === key ? '#E85D04' : '#64748B',
            borderBottom: tab === key ? '2px solid #E85D04' : '2px solid transparent',
            fontSize: 14,
          }}>{label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="admin-card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>
              Avg Prep Time by Restaurant
              <span style={{ fontSize: 12, fontWeight: 400, color: '#94A3B8', marginLeft: 8 }}>
                Green = on target (≤{TARGET_PREP_MINUTES}m), Yellow = moderate, Red = needs attention
              </span>
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis unit=" min" tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, n, p) => [`${v} min`, p.payload.fullName]}
                  contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)' }}
                />
                <ReferenceLine y={TARGET_PREP_MINUTES} stroke="#E85D04" strokeDasharray="4 4"
                  label={{ value: `Target ${TARGET_PREP_MINUTES}m`, fill: '#E85D04', fontSize: 11, position: 'right' }} />
                <Bar dataKey="avgPrepTime" shape={<CustomBar />} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="admin-table-wrapper">
            <div className="admin-table-toolbar">
              <span style={{ fontWeight: 600, fontSize: 14 }}>Restaurant Breakdown</span>
            </div>
            <Table columns={columns} data={restaurantBreakdown} />
          </div>
        </>
      )}

      {tab === 'trend' && (
        <div className="admin-card">
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>Avg Prep Time Trend (last 7 days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis unit=" min" tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={v => [`${v} min`]}
                contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)' }}
              />
              <Legend />
              <ReferenceLine y={TARGET_PREP_MINUTES} stroke="#E85D04" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="avgPrepTime" name="Avg Prep Time" stroke="#8B5CF6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="fastestTime" name="Fastest" stroke="#10B981" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
              <Line type="monotone" dataKey="slowestTime" name="Slowest" stroke="#EF4444" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PreparationAnalytics;
